import { Directive, ElementRef, HostListener, inject } from '@angular/core';

/**
 * Arrow-key navigation for the rail. Ported verbatim from evo-accounts
 * `shared/directives/menu-keyboard-navigation.directive.ts`.
 *
 * It reads `document` inside a keydown handler only, which is browser-only by
 * construction — the server never dispatches one — so there is no platform
 * guard here and none in the original either.
 *
 * The class names it queries (`.menu-link`, `.submenu`, `.disabled`) are a
 * contract with the rail's templates, not decoration. Renaming one there
 * silently breaks keyboard access here.
 */
@Directive({
  selector: '[appMenuKeyboardNav]',
  standalone: true,
})
export class MenuKeyboardNavigationDirective {
  private el = inject(ElementRef);

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    const element = this.el.nativeElement;
    const menuItems = element.querySelectorAll('.menu-link:not(.disabled):not([tabindex="-1"])');
    const currentIndex = Array.from(menuItems).findIndex(item => {
      const el = item as HTMLElement;
      return el === document.activeElement || el.contains(document.activeElement);
    });

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.focusNextItem(menuItems, currentIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.focusPreviousItem(menuItems, currentIndex);
        break;

      case 'ArrowRight': {
        // Focus first child menu item if current item has children
        if (currentIndex >= 0) {
          const currentItem = menuItems[currentIndex] as HTMLElement;
          const submenu = currentItem.parentElement?.querySelector('.submenu');
          if (submenu) {
            const firstChild = submenu.querySelector('.menu-link:not(.disabled)') as HTMLElement;
            if (firstChild) {
              event.preventDefault();
              firstChild.focus();
            }
          }
        }
        break;
      }

      case 'ArrowLeft': {
        // Focus parent menu item if current is in submenu
        if (currentIndex >= 0) {
          const currentItem = menuItems[currentIndex] as HTMLElement;
          const parentSubmenu = currentItem.closest('.submenu');
          if (parentSubmenu) {
            const parentItem = parentSubmenu.previousElementSibling as HTMLElement;
            if (parentItem && parentItem.classList.contains('menu-link')) {
              event.preventDefault();
              parentItem.focus();
            }
          }
        }
        break;
      }

      case 'Home':
        event.preventDefault();
        this.focusFirstItem(menuItems);
        break;

      case 'End':
        event.preventDefault();
        this.focusLastItem(menuItems);
        break;

      case 'Enter':
      case ' ': {
        if (currentIndex >= 0) {
          const item = menuItems[currentIndex] as HTMLElement;

          // Don't prevent default for anchor elements with href
          if (item.tagName === 'A' && item.hasAttribute('href')) {
            // Let the default link behavior work
            return;
          }

          // For buttons and other elements, prevent default and click
          event.preventDefault();
          item.click();
        }
        break;
      }

      case 'Escape':
        event.preventDefault();
        this.blurCurrentItem();
        break;

      case '/': {
        // Focus search input if exists
        event.preventDefault();
        const searchInput = document.querySelector('.search-input') as HTMLInputElement;
        if (searchInput) {
          searchInput.focus();
        }
        break;
      }

      // Add letter navigation for quick jumping
      default:
        if (event.key.length === 1 && /[a-zA-Z]/.test(event.key)) {
          this.focusItemByLetter(menuItems, event.key.toLowerCase());
        }
        break;
    }
  }

  private focusNextItem(menuItems: NodeListOf<Element>, currentIndex: number) {
    const nextIndex = currentIndex < menuItems.length - 1 ? currentIndex + 1 : 0;
    (menuItems[nextIndex] as HTMLElement)?.focus();
  }

  private focusPreviousItem(menuItems: NodeListOf<Element>, currentIndex: number) {
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : menuItems.length - 1;
    (menuItems[prevIndex] as HTMLElement)?.focus();
  }

  private focusFirstItem(menuItems: NodeListOf<Element>) {
    if (menuItems.length > 0) {
      (menuItems[0] as HTMLElement).focus();
    }
  }

  private focusLastItem(menuItems: NodeListOf<Element>) {
    if (menuItems.length > 0) {
      (menuItems[menuItems.length - 1] as HTMLElement).focus();
    }
  }

  private focusItemByLetter(menuItems: NodeListOf<Element>, letter: string) {
    const items = Array.from(menuItems);
    const currentIndex = items.findIndex(item => {
      const el = item as HTMLElement;
      return el === document.activeElement || el.contains(document.activeElement);
    });

    // Start searching from the next item after current
    const startIndex = currentIndex >= 0 ? currentIndex + 1 : 0;

    // Search from startIndex to end
    for (let i = startIndex; i < items.length; i++) {
      const item = items[i] as HTMLElement;
      const label = item.getAttribute('aria-label') || item.textContent || '';
      if (label.toLowerCase().startsWith(letter)) {
        item.focus();
        return;
      }
    }

    // If not found, search from beginning to startIndex
    for (let i = 0; i < startIndex; i++) {
      const item = items[i] as HTMLElement;
      const label = item.getAttribute('aria-label') || item.textContent || '';
      if (label.toLowerCase().startsWith(letter)) {
        item.focus();
        return;
      }
    }
  }

  private blurCurrentItem() {
    const activeElement = document.activeElement as HTMLElement;
    if (activeElement && this.el.nativeElement.contains(activeElement)) {
      activeElement.blur();
    }
  }
}
